import { FC, useEffect, useMemo, useState } from 'react';
import {
  Wrapper,
  FieldTable,
  Paging,
  usePaging,
  EllipsisSpan,
  useAsync,
  Tabs,
  CustomFilter,
  ShowInput,
} from '@maxtropy/components';
import { Form, Select, Input, Cascader, Tag, Space, Button, DatePicker } from 'antd';
import { getRoot } from '../../../api/device';
import { AlarmLevel, AlarmLevelColorDisplay, AlarmLevelDisplay, AlarmState, AlarmStateDisplay, AlarmStateColorDisplay,} from '@/shared/types';
import { formatOptionData, getChildNodesByParentIds } from '@/shared/components/CascadingMultipleSelector/utils';
import { apiV2RuleAlarmLogPagePost, V2RuleAlarmLogPagePostResponse, apiV2RuleAlarmLogAlarmLevelCountPost,V2RuleAlarmLogAlarmLevelCountPostResponse } from '@maxtropy/tody-mgmt-apis-v2';
import { getPhysicalModelList, PhysicalModelRes } from '@/api/device';
import { TabKeyEnum, TabKeyStateDisplay } from '../utils';
import { DefaultOptionType } from 'antd/es/cascader';
import { getDeviceTypeData } from '@/api/attribute';
import { useWatch } from 'antd/es/form/Form';
import { Link } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import GradeSwitch from './GradeSwitch';
import EnergyAssetInfoModal from './EnergyAssetInfoModal';

import {
  AlarmLogRequestB,
  AlarmLogResponse,
  getAlarmLogList,
  getOuListByRootMcid,
  OuResponse,
} from '../../../api/alarm';
import RecordPagination from '@/components/RecordPagination';

const { RangePicker } = DatePicker;

const dateFormat = 'YYYY/MM/DD HH:mm';
const displayDateFormat = 'YYYY/MM/DD HH:mm:ss';

const routes = [{ name: 'Iot配置' }, { name: '报警推送' }, { name: '设备报警记录' }];

interface FilterParams {
  tenantMcid?: string;
  ouId?: number;
  codeOrName?: string;
  state?: number;
  time?: [Dayjs, Dayjs];
}
export type AlarmProps = Exclude<V2RuleAlarmLogPagePostResponse['list'], undefined>[number];
export type deviceAssetsResponse = AlarmProps['deviceAssets']

type SearchParams = Omit<AlarmLogRequestB, 'page' | 'size'>;

const AlarmRecordList: FC = () => {
  const [form] = Form.useForm();

  const [totalCount, setTotalCount] = useState<number>(); // 总页数
  const [isSortValue, setIsSortValue] = useState<boolean>(false); // 是否传sortvalue
  const [currentPage, setCurrentPage] = useState<number>(1); // 当前页数
  const [sortValues, setSortValues] = useState<any[]>();
  const [pageSize, setPageSize] = useState<number>(50); // 每页多少条

  const [searchParams, setSearchParams] = useState<SearchParams>({});

  const tenant = useAsync(getRoot, []);
  const [tenantMcid, setTenantMcid] = useState<string>();
  const deviceTypeData = useAsync(getDeviceTypeData);
  const [physicalModelList, setPhysicalModelList] = useState<PhysicalModelRes[]>([]); // all 物模型型号列表
  const [energyAssetInfo, setEnergyAssetInfo] = useState<deviceAssetsResponse>([]);
  const [tabKey, setTabKey] = useState(TabKeyEnum.UNRESTORED_ALARM);
    const [open, setOpen] = useState<boolean>(false);
  const [gradeDatas, setGradeDatas] = useState<V2RuleAlarmLogAlarmLevelCountPostResponse>();
  const deviceTypeId = useWatch('deviceTypeId', form);
  const [levels, setLevels] = useState<number[]>([]);

  useEffect(() => {
    // 物模型型号
    if (deviceTypeId) {
      const typeIds = getChildNodesByParentIds(deviceTypeId, deviceTypeData?.tree);
      getPhysicalModelList({ deviceTypeIds: typeIds }).then(res => setPhysicalModelList(res));
    } else {
      getPhysicalModelList({}).then(res => setPhysicalModelList(res));
    }
  }, [deviceTypeId]);

  const columns = [
    {
      title: '设备名称',
      dataIndex: 'deviceName',
      ellipsis: { showTitle: true },
      width: 180,
      fixed: true,
      render: (v: string, record: AlarmProps) => {
        return (
          <Link to={`/device/manage/device/${record?.deviceId}/detail`} target="_blank">
            <EllipsisSpan value={v} />
          </Link>
        );
      },
    },
    {
      title: '报警等级',
      dataIndex: 'alarmLevel',
      width: 100,
      ellipsis: { showTitle: true },
      fixed: true,
       render: (v: AlarmLevel) => (
        <EllipsisSpan value={<Tag color={AlarmLevelColorDisplay[v]}>{AlarmLevelDisplay[v]}</Tag>} />
      ),
    },
    {
      title: '报警状态',
      dataIndex: 'state',
      width: 100,
      ellipsis: { showTitle: true },
      fixed: true,
      render: (v: AlarmState) => (
      <EllipsisSpan value={<Tag color={AlarmStateColorDisplay[v]}> {AlarmStateDisplay[v]}</Tag>} />
      ),

    },
    {
      title: '规则名称',
      dataIndex: 'ruleName',
      width: 180,
      fixed: true,
      render: (v: string) => <EllipsisSpan value={v} />,
    },

    {
      title: '报警信息',
      dataIndex: 'alarmName',
      width: 120,
      fixed: true,
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '报警时间',
      width: 180,
      dataIndex: 'alarmTime',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v ? dayjs(v).format(displayDateFormat) : undefined} />,
    },
    {
      title: '恢复时间',
      width: 180,
      dataIndex: 'recoveryTime',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v ? dayjs(v).format(displayDateFormat) : '未恢复'} />,
    },
    {
      title: '持续时间',
      width: 180,
      dataIndex: 'durationTime',
      ellipsis: { showTitle: true },
      render: (v: string, record: any) => {
        const { alarmTime, recoveryTime } = record;
        if (!alarmTime || !recoveryTime) return <EllipsisSpan value={'--'} />;

        const start = dayjs(alarmTime);
        const end = dayjs(recoveryTime);
        const diffMs = end.diff(start);

        // 总分钟数
        const totalMinutes = Math.floor(diffMs / (1000 * 60));
        const days = Math.floor(totalMinutes / (60 * 24));
        const hours = Math.floor((totalMinutes - days * 24 * 60) / 60);
        const minutes = totalMinutes - days * 24 * 60 - hours * 60;

        let durationText = '';
        if (days > 0) durationText += `${days}天`;
        if (hours > 0) durationText += `${hours}时`;
        if (minutes > 0) durationText += `${minutes}分`;

        return <EllipsisSpan value={durationText} />;
      },
    },
    {
      title: '已读时间',
      width: 180,
      dataIndex: 'readTime',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v ? dayjs(v).format(displayDateFormat) : undefined} />,
    },
    {
      title: '规则编号',
      dataIndex: 'alarmCode',
      width: 180,
      ellipsis: { showTitle: true },
      fixed: true,
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '设备编号',
      dataIndex: 'deviceCode',
      width: 180,
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '所属类目',
      dataIndex: 'deviceType',
      width: 180,
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '物模型',
      dataIndex: 'physicalModel',
      width: 120,
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '能源资产信息',
      dataIndex: 'deviceAssets',
      ellipsis: { showTitle: true },
      width: 120,
      render: (v: string, record: any) => {
        return !record.deviceAssets || record.deviceAssets.length === 0 ? (
          '暂无'
        ) : (
          <Button type="link" onClick={() => showEnergyAssetInfo(record.deviceAssets)}>
            点击查看
          </Button>
        );
      },
    },
    // {
    //   title: '所属租户',
    //   dataIndex: 'tenantCode',
    //   ellipsis: { showTitle: true },
    //   render: (v: string, record: any) => <EllipsisSpan value={`${v} ${record.tenantName}`} />,
    // },
  ];

  const firstMcid = useMemo(() => {
    if (Array.isArray(tenant) && tenant.length) {
      const first = tenant[0];
      return first.mcid;
    }
  }, [tenant]);

  useEffect(() => {
    if (firstMcid) {
      setTenantMcid(firstMcid);
      setSearchParams({ ...searchParams, tenantMcid: firstMcid });
      form.setFieldsValue({
        tenantMcid: firstMcid,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstMcid, form]);

  const [ou, setOu] = useState<OuResponse[]>([]);

  useEffect(() => {
    // 重新获取ou
    if (tenantMcid) {
      getOuListByRootMcid(tenantMcid).then(res => {
        setOu(res);
      });
    }
  }, [tenantMcid]);

  const firstOuId = useMemo(() => {
    if (Array.isArray(ou) && ou.length) {
      const first = ou[0];
      return first.id;
    } else {
      return undefined;
    }
  }, [ou]);

  useEffect(() => {
    if (firstOuId) {
      form.setFieldsValue({
        ouId: firstOuId,
      });
      setSearchParams({ ...searchParams, ouId: firstOuId });
    } else {
      form.setFieldsValue({
        ouId: undefined,
      });
      setSearchParams({ ...searchParams, ouId: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstOuId, form]);

  const [records, setRecords] = useState<AlarmProps[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  useEffect(() => {
    if (searchParams.tenantMcid && searchParams.ouId) {
      setLoading(true);
      apiV2RuleAlarmLogPagePost({
        ...searchParams,
        levels,
        sortValues: isSortValue ? sortValues : undefined,
        page: currentPage,
        size: pageSize,
        state:tabKey
      }).then(res => {
        setLoading(false);
        if (res) {
          if (res.useSearchAfter) {
            setSortValues(res.sortValues);
            setIsSortValue(true);
          } else {
            setSortValues(undefined);
            setIsSortValue(false);
          }
          setRecords(res.list ?? []);
          setTotalCount(res.total);
        }
      });
      apiV2RuleAlarmLogAlarmLevelCountPost({
      ...searchParams,
      levels: [],
      sortValues: isSortValue ? sortValues : undefined,
      page: currentPage,
      size: pageSize,
      state: tabKey,
    }).then(res => {
      setGradeDatas(res);
    });
    }
  }, [currentPage, pageSize, searchParams, setTotalCount,tabKey,levels]);

  const onFinish = (value: FilterParams) => {
    setIsSortValue(false);
    const endtime = value.time ? dayjs(value.time[1]).endOf('day') : undefined;
    const currentTime = dayjs();
    const params: SearchParams = {
      tenantMcid: value.tenantMcid,
      ouId: value.ouId,
      codeOrName: value.codeOrName,
      state: value.state,
      startTime: value.time ? dayjs(value.time[0]).valueOf() : undefined,
      endTime: endtime ? (endtime.isAfter(currentTime) ? currentTime : endtime).valueOf() : undefined,
    };
    setSearchParams(params);
    setCurrentPage(1);
  };

  const onReset = () => {
    const params: FilterParams = {
      tenantMcid: firstMcid,
      ouId: undefined,
      codeOrName: undefined,
      state: undefined,
      time: undefined,
    };
    if (tenantMcid === firstMcid) {
      params.ouId = firstOuId;
    }
    setSearchParams(params);
    setTenantMcid(firstMcid);
    setCurrentPage(1);
  };

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      dataIndex: 'operation',
      width: 250,
      fixed: 'right' as const,
      render: (value: undefined, record: AlarmProps) => {
        return (
          <Space size={16}>
            <Button style={{ paddingLeft: 0, paddingRight: 0 }} type="link">
              <Link to={`/device/alarm/record/detail/${record.id}`}>查看</Link>
            </Button>
          </Space>
        );
      },
    },
  ];

  const showEnergyAssetInfo = (val: deviceAssetsResponse) => {
    setOpen(true);
    setEnergyAssetInfo(val);
  };

  const filter = (inputValue: string, path: DefaultOptionType[]) =>
    path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1);

  const objectModalTypeOptions = useMemo(() => {
    if (physicalModelList && physicalModelList.length > 0) {
      return physicalModelList.map(i => ({ label: `${i.modelNo}-${i.manufacturerName}`, value: i.id }));
    }
    return [];
  }, [physicalModelList]);

  const items = [
    {
      key: TabKeyEnum.UNRESTORED_ALARM,
      label: TabKeyStateDisplay[TabKeyEnum.UNRESTORED_ALARM],
    },
    {
      key: TabKeyEnum.RESTORED_ALARM,
      label: TabKeyStateDisplay[TabKeyEnum.RESTORED_ALARM],
    },
  ];

  const filters = (
    <div>
      <Tabs defaultActiveKey="1" items={items} selectedLinePlacement="top" onChange={setTabKey} />
      <div style={{ padding: '0 20px' }}>
        <CustomFilter
          form={form}
          onFinish={val => {
            onFinish(val as FilterParams);
          }}
          onReset={onReset}
          collapseOpen={false}
        >
          <Form.Item name="tenantMcid" label="所属租户" initialValue={firstMcid}>
            <Select
              style={{ width: '100%' }}
              placeholder="请选择"
              onChange={v => {
                setTenantMcid(v as string);
              }}
            >
              {tenant.map(i => (
                <Select.Option key={i.mcid} value={i.mcid}>
                  {i.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="ouId" label="生产运营单元" initialValue={firstOuId}>
            <Select style={{ width: '100%' }} placeholder="请选择">
              {ou.map(i => (
                <Select.Option key={i.id} value={i.id}>
                  {i.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="ruleInfo" label="规则编码/名称" rules={[{ max: 30, message: '请输入规则编码或者名称查询' }]}>
            <Input placeholder="请输入规则编码或者名称查询" />
          </Form.Item>

          <Form.Item name="deviceName" label="设备名称">
            <Input placeholder="请输入设备名称查询" />
          </Form.Item>

          <Form.Item name="time" label="报警日期">
            <RangePicker
              style={{ width: '100%' }}
              showTime={{
                format: 'HH:mm',
              }}
              disabledDate={date => {
                return dayjs().isBefore(date);
              }}
              format={dateFormat}
            />
          </Form.Item>
          {/* <Form.Item name="state" label="记录状态">
            <Select style={{ width: '100%' }} placeholder="请选择">
              <Select.Option value={AlarmState.RECOVER}>{AlarmStateDisplay[AlarmState.RECOVER]}</Select.Option>
              <Select.Option value={AlarmState.UNRECOVERED}>{AlarmStateDisplay[AlarmState.UNRECOVERED]}</Select.Option>
            </Select>
          </Form.Item> */}

          <Form.Item name="alarmName" label="报警信息" rules={[{ max: 30, message: '请输入报警信息查询' }]}>
            <Input placeholder="请输入报警信息查询" />
          </Form.Item>

          <Form.Item
            name="deviceAssetCode"
            label="能源资产编号"
            rules={[{ max: 30, message: '能源资产编号最多30个字符' }]}
          >
            <Input placeholder=" 请输入能源资产编号" />
          </Form.Item>

          <Form.Item name="deviceCode" label="设备编号">
            <Input placeholder="请输入设备编号查询" />
          </Form.Item>

          <Form.Item name="deviceTypeId" label="所属类目">
            <Cascader
              onChange={() => form.setFieldValue('physicalModelId', undefined)}
              options={formatOptionData(deviceTypeData)}
              allowClear={false}
              fieldNames={{ children: 'child' }}
              multiple
              maxTagCount="responsive"
              showSearch={{ filter }}
              placeholder={'请选择所属类目'}
            />
          </Form.Item>

          <Form.Item name="physicalModelId" label="物模型型号">
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="label"
              showSearch
              options={objectModalTypeOptions}
              placeholder={'请选择'}
            />
          </Form.Item>
        </CustomFilter>
      </div>
    </div>
  );

  return (
    <Wrapper routes={routes} filters={filters} filtersStyle={{ padding: 0 }}>
      <GradeSwitch gradeDatas={gradeDatas!} setLevels={setLevels} />
      <FieldTable
        adjustField={true}
        adjustWidth={true}
        freezeHide={true}
        cacheKey={'device_List_table'}
        rowKey={'id'}
        sticky={{
          getContainer: () => {
            return document.querySelector('.central-menu-content') as HTMLElement;
          },
        }}
        scroll={{ x: 1900 }}
        loading={loading}
        dataSource={records}
        columns={buildColumns}
      />
      {/* <Table sticky loading={loading} scroll={{ x: 1900 }} columns={buildColumns} dataSource={records} /> */}
      {totalCount ? (
        <RecordPagination
          totalCount={totalCount}
          setPageSize={setPageSize}
          pageSize={pageSize}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          setIsSortValue={setIsSortValue}
        />
      ) : null}
     {open && <EnergyAssetInfoModal setOpen={setOpen} energyAssetInfo={energyAssetInfo}/>}
    </Wrapper>
  );
};

export default AlarmRecordList;
